import { motion } from "framer-motion"
import { useState, useEffect, type CSSProperties } from "react"
import { useNavigate } from "react-router-dom"
import { LogFeed } from "../../components/LogFeed"
import { QR } from "../../components/QR"
import { useControlCenter } from "../../AppShell"
import { useEventLog } from "../../hooks/useApi"
import { useDemoMode } from "../../demo/DemoProvider"
import { useTheme } from "../../theme/ThemeProvider"
import { useConnectStripe } from "../../hooks/useConnectStripe"
import { MetricCard } from "../../components/intelligence"
import { useTenantAnalytics } from "../../hooks/useIntelligence"
import type { SenderChannelStatus } from "../../types"

export default function DashboardPage() {
  const { tenant } = useControlCenter()
  const { data: events = [] } = useEventLog()
  const { demo } = useDemoMode()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const isFairy = theme === "fairy"
  const { connect: handleConnectStripe, connecting: stripeLoading, error: stripeError } = useConnectStripe()

  // Intelligence hooks
  const { data: analytics } = useTenantAnalytics()

  useEffect(() => {
    // Guard: If tenant has no number assigned, redirect to onboarding
    // We check for a valid phone number (starts with + and has digits) rather than sms_ready
    // because sms_ready depends on sender_profiles which may not be populated yet
    const hasValidNumber = tenant.number_e164 && tenant.number_e164.startsWith("+") && tenant.number_e164.length > 10
    if (!hasValidNumber && !tenant.demo) {
      navigate("/onboard/number", { replace: true })
    }
  }, [tenant, navigate])

  const smsStatus = tenant.sender_status?.sms
  const needsSetup = !tenant.payments_ready || !tenant.sms_ready
  const persona = isFairy ? "Fairy" : "Demon"
  const personaPlural = isFairy ? "Fairies" : "Demons"

  const panelBackground = isFairy ? "rgba(255,255,255,0.96)" : "rgba(18,18,20,0.82)"
  const panelBorder = isFairy ? "1px solid rgba(46,214,181,0.4)" : "1px solid var(--rail)"
  const panelShadow = isFairy ? "0 18px 32px rgba(46,214,181,0.12)" : "0 0 22px rgba(255,43,79,0.18)"

  return (
    <div style={{ display: "grid", gap: "clamp(16px, 3vw, 24px)" }}>
      <SummonerCard
        demo={demo}
        background={panelBackground}
        border={panelBorder}
        shadow={panelShadow}
        isFairy={isFairy}
        persona={persona}
        paymentsReady={tenant.payments_ready}
        onOpenChat={() => navigate("/app/customers")}
        onConnectStripe={handleConnectStripe}
        stripeLoading={stripeLoading}
        stripeError={stripeError}
      />
      {needsSetup ? (
        <MessagePanel
          number={tenant.number_e164}
          background={panelBackground}
          border={panelBorder}
          shadow={panelShadow}
          isFairy={isFairy}
          persona={persona}
          personaPlural={personaPlural}
          smsStatus={smsStatus}
        />
      ) : null}

      {/* Intelligence Section */}
      <section
        style={{
          background: panelBackground,
          border: panelBorder,
          padding: "clamp(20px, 4vw, 28px)",
          display: "flex",
          flexDirection: "column",
          gap: "clamp(16px, 3vw, 24px)",
          boxShadow: panelShadow,
        }}
      >
        <header
          className="mono"
          style={{
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: isFairy ? "#1f2d42" : "var(--text)",
          }}
        >
          Performance
        </header>

        {/* Metric Cards Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "12px",
          }}
        >
          <MetricCard
            label="Collection Rate"
            value={analytics?.collection_rate ?? 0}
            unit="%"
          />
          <MetricCard
            label="Avg Days to Pay"
            value={analytics?.avg_days_to_payment ?? 0}
          />
          <MetricCard
            label="Outstanding"
            value={`$${(analytics?.outstanding_amount ?? 0).toLocaleString()}`}
          />
        </div>

      </section>

      <section
        style={{
          background: panelBackground,
          border: panelBorder,
          padding: "clamp(20px, 4vw, 28px)",
          display: "flex",
          flexDirection: "column",
          gap: "clamp(12px, 2.5vw, 18px)",
          boxShadow: panelShadow,
        }}
      >
        <header
          className="mono"
          style={{
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: isFairy ? "#1f2d42" : "var(--text)",
          }}
        >
          Log Feed
        </header>
        <LogFeed items={events} />
      </section>
    </div>
  )
}

function SummonerCard({
  demo,
  background,
  border,
  shadow,
  isFairy,
  persona,
  paymentsReady,
  onOpenChat,
  onConnectStripe,
  stripeLoading,
  stripeError,
}: {
  demo: boolean
  background: string
  border: string
  shadow: string
  isFairy: boolean
  persona: string
  paymentsReady: boolean
  onOpenChat: () => void
  onConnectStripe: () => void
  stripeLoading: boolean
  stripeError: string | null
}) {
  // When Stripe is NOT connected, show attention card with red background
  if (!paymentsReady) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        style={{
          display: "grid",
          gap: "clamp(12px, 2.5vw, 18px)",
          background: "rgba(255,43,79,0.95)",
          border: "2px solid #0a0a0b",
          padding: "clamp(20px, 4vw, 28px)",
          boxShadow: "0 0 32px rgba(255,43,79,0.35)",
        }}
      >
        <div
          className="mono"
          style={{
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            color: "#0a0a0b",
          }}
        >
          {persona.toUpperCase()} WALKTHROUGH
        </div>
        <p className="small" style={{ textTransform: "none", color: "#0a0a0b", lineHeight: 1.5 }}>
          Before your {persona} can send real invoices, we need to connect Stripe and set up your billing plan. 
          This ensures we can charge for SalesDemon and route payouts correctly.
        </p>
        {stripeError ? (
          <div className="mono small" style={{ color: "#0a0a0b", background: "rgba(0,0,0,0.15)", padding: "8px 12px" }}>
            {stripeError}
          </div>
        ) : null}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <motion.button
            whileTap={{ scale: 0.95 }}
            className="mono"
            onClick={onConnectStripe}
            disabled={stripeLoading}
            style={{
              border: "2px solid #0a0a0b",
              padding: "12px 16px",
              background: "#0a0a0b",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              cursor: stripeLoading ? "wait" : "pointer",
              color: "#fff",
              minHeight: "44px",
              opacity: stripeLoading ? 0.7 : 1,
            }}
          >
            {stripeLoading ? "Opening Stripe…" : "Connect Stripe"}
          </motion.button>
        </div>
      </motion.section>
    )
  }

  // When Stripe IS connected, show the normal walkthrough card
  return (
    <motion.section
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      style={{
        display: "grid",
        gap: "clamp(12px, 2.5vw, 18px)",
        background,
        border,
        padding: "clamp(20px, 4vw, 28px)",
        boxShadow: shadow,
      }}
    >
      <div
        className="mono"
        style={{
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          color: isFairy ? "#1f2d42" : "var(--text)",
        }}
      >
        <img
          src={isFairy ? "/assets/pixel/icon_walk_fairy.png" : "/assets/pixel/icon_walk_demon.png"}
          alt=""
          className="pixel"
          style={{ width: 24, height: 24 }}
        />
        {persona} Walkthrough
      </div>
      <p className="small" style={{ textTransform: "none", color: isFairy ? "#4b5a6d" : "var(--muted)" }}>
        See how it works: ask for a sample invoice, or add a test customer and watch your {persona.toLowerCase()} reply. {demo ? "Demo mode — nothing is sent to real customers." : "Live mode — messages go to real customers."}
      </p>
      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        <motion.button
          whileTap={{ scale: 0.95 }}
          className="mono"
          onClick={onOpenChat}
          style={{
            border: "2px solid var(--rail)",
            padding: "12px 16px",
            background: isFairy ? "rgba(244,255,251,0.92)" : "transparent",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            cursor: "pointer",
            color: isFairy ? "#1f2d42" : "var(--text)",
            boxShadow: isFairy ? "0 10px 24px rgba(46,214,181,0.18)" : "none",
            minHeight: "44px",
          }}
        >
          Ask for a sample invoice
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          className="mono"
          onClick={onOpenChat}
          style={{
            border: "2px solid rgba(255,43,79,0.32)",
            padding: "12px 16px",
            background: isFairy ? "rgba(255,214,226,0.45)" : "transparent",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            cursor: "pointer",
            color: isFairy ? "#5c1f2d" : "var(--text)",
            boxShadow: isFairy ? "0 10px 24px rgba(252,165,179,0.18)" : "none",
            minHeight: "44px",
          }}
        >
          Add a test customer
        </motion.button>
      </div>
    </motion.section>
  )
}

function MessagePanel({
  number,
  background,
  border,
  shadow,
  isFairy,
  persona,
  personaPlural,
  smsStatus,
}: {
  number: string
  background: string
  border: string
  shadow: string
  isFairy: boolean
  persona: string
  personaPlural: string
  smsStatus?: SenderChannelStatus | null
}) {
  const smsBackground = isFairy ? "rgba(244,255,251,0.92)" : "transparent"
  const smsColor = isFairy ? "#1f2d42" : "var(--text)"
  const smsShadow = isFairy ? "0 10px 24px rgba(46,214,181,0.18)" : "none"
  const smsReady = (smsStatus?.status ?? "").toLowerCase() === "ready"
  const smsHref = `sms:${number}?body=${encodeURIComponent(`Hi ${persona}`)}`
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(number)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  return (
    <section
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "24px",
        background,
        border,
        padding: "clamp(20px, 4vw, 28px)",
        alignItems: "flex-start",
        justifyContent: "space-between",
        boxShadow: shadow,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", flex: "1 1 320px", minWidth: 0 }}>
        <div
          className="mono"
          style={{ letterSpacing: "0.14em", textTransform: "uppercase", color: isFairy ? "#1f2d42" : "var(--text)" }}
        >
          Message panel
        </div>
        <div className="mono small" style={{ color: isFairy ? "#4b5a6d" : "var(--muted)" }}>
          Channel readiness
        </div>
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <ReadinessItem
            label="SMS"
            ready={smsReady}
            detail={smsStatus?.sender_identifier ? `Assigned ${smsStatus.sender_identifier}` : "Awaiting assignment"}
            pendingActions={smsStatus?.pending_actions ?? []}
            isFairy={isFairy}
          />
        </ul>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
          <div
            className="mono"
            style={{ fontSize: "24px", letterSpacing: "0.18em", color: isFairy ? "#1f2d42" : "var(--text)" }}
          >
            {number}
          </div>
          <motion.button
            whileTap={{ scale: 0.96 }}
            type="button"
            onClick={handleCopy}
            className="mono small"
            style={{
              padding: "10px 16px",
              border: "2px solid var(--rail)",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              background: smsBackground,
              color: smsColor,
              boxShadow: smsShadow,
              minHeight: "40px",
              cursor: "pointer",
            }}
          >
            {copied ? "Copied" : "Copy"}
          </motion.button>
        </div>
        <p className="small" style={{ textTransform: "none", color: isFairy ? "#4b5a6d" : "var(--muted)" }}>
          Text your {persona} from your phone. Share the pay link once {personaPlural.toLowerCase()} reply.
        </p>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <a
            href={smsHref}
            className="mono"
            style={{
              ...linkStyle,
              background: smsBackground,
              color: smsColor,
              boxShadow: smsShadow,
              minHeight: "44px",
            }}
            target="_blank"
            rel="noreferrer"
          >
            SMS
          </a>
        </div>
      </div>
      <div style={{ flex: "0 0 auto", marginLeft: "auto", width: "min(180px, 40vw)", alignSelf: "center" }}>
        <QR value={smsHref} />
      </div>
    </section>
  )
}

function ReadinessItem({
  label,
  ready,
  detail,
  pendingActions,
  isFairy,
}: {
  label: string
  ready: boolean
  detail: string
  pendingActions: string[]
  isFairy: boolean
}) {
  const accent = ready ? (isFairy ? "#0f766e" : "#f97316") : isFairy ? "#dc2626" : "#fca5a5"
  return (
    <li
      style={{
        border: `1px dashed ${accent}`,
        padding: "10px 12px",
        borderRadius: 6,
        background: ready ? (isFairy ? "rgba(16,185,129,0.08)" : "rgba(252,211,77,0.08)") : "transparent",
      }}
    >
      <div className="mono" style={{ letterSpacing: "0.1em", textTransform: "uppercase", fontSize: 12, color: accent }}>
        {label} · {ready ? "Ready" : "Pending"}
      </div>
      <div className="mono small" style={{ color: isFairy ? "#4b5a6d" : "var(--muted)" }}>{detail}</div>
      {!ready && pendingActions.length ? (
        <div className="mono tiny" style={{ marginTop: 6, color: accent }}>
          Next: {pendingActions.join(", ")}
        </div>
      ) : null}
    </li>
  )
}

const linkStyle: CSSProperties = {
  padding: "10px 16px",
  border: "2px solid var(--rail)",
  textTransform: "uppercase",
  letterSpacing: "0.14em",
}
